export type SeoReviewStatus = "pass" | "fail" | "pending"

export interface SeoReviewFinding {
  ruleId?: string
  severity?: string
  message?: string
}

export interface SeoReviewGateResult {
  passed: boolean
  issues: readonly string[]
  findings?: readonly SeoReviewFinding[]
}

export interface SeoReviewContent {
  metaTitle?: string
  h1?: string
  body?: readonly unknown[]
  faq?: readonly unknown[]
  internalLinks?: readonly unknown[]
  evidence?: readonly unknown[]
}

export interface SeoReviewItem {
  label: string
  status: SeoReviewStatus
  detail: string
}

export interface SeoPageReviewSummary {
  items: readonly SeoReviewItem[]
  blockers: readonly string[]
  nextAction: string
}

export function presentSeoFindings<T extends { ruleId?: string; severity?: string }>(
  findings: readonly T[],
): {
  findings: readonly T[]
  blockerCount: number
  warningCount: number
} {
  const hasRoleSpecificMediaFinding = findings.some(
    (finding) => finding.ruleId === "media.hero_required" || finding.ruleId === "media.example_required",
  )
  const presented = findings.filter((finding) => !(hasRoleSpecificMediaFinding && finding.ruleId === "media.minimum"))
  return {
    findings: presented,
    blockerCount: presented.filter((finding) => finding.severity === "error").length,
    warningCount: presented.filter((finding) => finding.severity === "warning").length,
  }
}

function hasBlockingFinding(gateResult: SeoReviewGateResult | null | undefined, prefix: string): boolean {
  return Boolean(
    gateResult?.findings?.some(
      (finding) => finding.severity === "error" && Boolean(finding.ruleId?.startsWith(prefix)),
    ),
  )
}

export function buildSeoPageReviewSummary({
  content,
  gateResult,
  renderedMediaCount,
}: {
  content: SeoReviewContent
  gateResult: SeoReviewGateResult | null | undefined
  renderedMediaCount: number
}): SeoPageReviewSummary {
  const coreContentReady = Boolean(
    content.metaTitle?.trim() &&
      content.h1?.trim() &&
      content.body?.length &&
      content.faq?.length &&
      content.internalLinks?.length,
  )
  const evidenceCount = content.evidence?.length ?? 0
  const evidenceBlocked = hasBlockingFinding(gateResult, "evidence.")
  const mediaBlocked = hasBlockingFinding(gateResult, "media.")
  const findingBlockers =
    gateResult?.findings
      ?.filter((finding) => finding.severity === "error")
      .map((finding) => finding.message)
      .filter((message): message is string => Boolean(message)) ?? []
  const blockers = findingBlockers.length > 0 ? findingBlockers : (gateResult?.issues ?? [])

  const evidenceStatus: SeoReviewStatus = evidenceBlocked
    ? "fail"
    : evidenceCount > 0 || gateResult
      ? "pass"
      : "pending"
  const mediaStatus: SeoReviewStatus = mediaBlocked ? "fail" : gateResult ? "pass" : "pending"
  const gateStatus: SeoReviewStatus = !gateResult ? "pending" : gateResult.passed ? "pass" : "fail"

  let nextAction = "Run Gate to create a publication checklist."
  if (gateResult?.passed) {
    nextAction = "Ready for release review. Approval and activation remain explicit."
  } else if (blockers.length > 0) {
    nextAction = `Resolve ${blockers.length} publication blocker${blockers.length === 1 ? "" : "s"}. First: ${blockers[0]}`
  }

  let evidenceDetail = "Evidence requirements have not been checked."
  if (evidenceCount > 0) {
    evidenceDetail = `${evidenceCount} evidence reference${evidenceCount === 1 ? "" : "s"} attached.`
  } else if (gateResult && !evidenceBlocked) {
    evidenceDetail = "No evidence required by the current gate."
  }

  let mediaDetail = "No approved rendered assets are attached."
  if (renderedMediaCount > 0) {
    mediaDetail = `${renderedMediaCount} rendered asset${renderedMediaCount === 1 ? "" : "s"} attached.`
  } else if (gateResult && !mediaBlocked) {
    mediaDetail = "No media required by the current gate."
  }

  return {
    items: [
      {
        label: "Core content",
        status: coreContentReady ? "pass" : "fail",
        detail: coreContentReady
          ? "Title, heading, body, FAQ, and internal links are filled."
          : "Required copy is incomplete.",
      },
      {
        label: "Evidence",
        status: evidenceStatus,
        detail: evidenceDetail,
      },
      {
        label: "Media",
        status: mediaStatus,
        detail: mediaDetail,
      },
      {
        label: "Quality gate",
        status: gateStatus,
        detail: !gateResult
          ? "Not checked yet."
          : gateResult.passed
            ? "Publication checks pass."
            : "Publication is blocked.",
      },
    ],
    blockers,
    nextAction,
  }
}
